import { extractErrorAttributes } from './error-extractor';
import {
  type Attributes,
  type LogInput,
  type Logger,
  type LoggerAdapter,
  type LogRecord,
  type Severity,
  SEVERITY,
} from './logger-types';
import { getContextAttributes } from './middleware/context-enricher';
import { scrubPii } from './middleware/pii-scrubber';

type AdapterSource = () => LoggerAdapter;

export class DefaultLogger implements Logger {
  // adapterSource is read on every emit so a later setLoggerAdapter() — in
  // tests — affects loggers already obtained at module scope.
  constructor(
    private readonly adapterSource: AdapterSource,
    readonly scope: string,
    private readonly boundAttributes: Attributes = {}
  ) {}

  error(message: string, input?: LogInput): void {
    this.emit(SEVERITY.ERROR, message, input);
  }

  warn(message: string, input?: LogInput): void {
    this.emit(SEVERITY.WARN, message, input);
  }

  info(message: string, input?: LogInput): void {
    this.emit(SEVERITY.INFO, message, input);
  }

  debug(message: string, input?: LogInput): void {
    this.emit(SEVERITY.DEBUG, message, input);
  }

  child(boundAttributes: Attributes): Logger {
    return new DefaultLogger(this.adapterSource, this.scope, {
      ...this.boundAttributes,
      ...boundAttributes,
    });
  }

  private emit(severity: Severity, body: string, input?: LogInput): void {
    const merged: Attributes = {
      ...getContextAttributes(),
      ...this.boundAttributes,
      ...(input?.error !== undefined ? extractErrorAttributes(input.error) : {}),
      ...(input?.attributes ?? {}),
    };

    const record: LogRecord = {
      severity,
      body,
      scope: this.scope,
      attributes: scrubPii(merged),
      timestamp: Date.now(),
    };
    this.adapterSource().emit(record);
  }
}
